import {keepPreviousData, useQuery} from "@tanstack/react-query";

import {getPagination} from "@/src/modules/core/lib";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";

import {mergeCitationsListFilters} from "../lib/merge-citations-list-filters";
import {citationsService} from "../services";

type UseCitationsParams = {
  rows: number;
  page: number;
  filters?: Record<string, unknown>;
  sort?: string;
  enabled?: boolean;
};

export function useCitations({
  rows,
  page,
  filters = {},
  sort = "-citation_date",
  enabled = true,
}: UseCitationsParams) {
  const userProperty = useStoredUserProperty();
  const filter = mergeCitationsListFilters(userProperty?.id, filters);

  const options = {
    page: {limit: rows, offset: (page - 1) * rows},
    filter,
    sort,
  };

  const {data, isSuccess, isPending, isError, error, isFetching, refetch} = useQuery({
    queryKey: ["citations", options],
    queryFn: () => citationsService.getCitations(options),
    retry: false,
    enabled: enabled && Boolean(userProperty?.id),
    placeholderData: keepPreviousData,
  });

  const citations = isSuccess && data ? data.data : [];
  const totalRecords = data?.meta?.total ?? 0;
  const {start, end} = getPagination(totalRecords, rows, page);

  return {
    citations,
    totalRecords,
    start,
    end,
    isSuccess,
    isPending,
    isError,
    error,
    isFetching,
    refetch,
  };
}
